import {sleep} from '@benchling/js-utils';

import {BenchlingBlob} from 'schemas/tags/BlobConstants';
import * as net from 'util/Net';
import * as TaskRunner from 'util/TaskRunner';

import {InstrumentData, InstrumentQuery, InstrumentQueryStatus} from './InstrumentDataConstants';

const ITERATION_WAIT_TIME = 5 * 1000; // 5 seconds
const MAX_WAIT_TIME = 5 * 60 * 1000; // 5 minutes
const MAX_RETRY_ATTEMPTS = 100;

export function searchInstrumentData(data: {
  searchFilter: string;
  connectionFilter: Array<string>;
  connectionSchemaFilter: Array<string>;
  nextToken: string;
  fileTypeFilter: string;
  instrumentQueryFilter: Array<string>;
  transformDataTypeFilter: string | null;
}): Promise<{instrumentData: Array<InstrumentData>; nextToken: string}> {
  return net.postJSONPromise({url: '/1/api/instruments/instrument-data', data});
}

export function createBlobs(instrumentDataIds: Array<string>): JQueryPromise<Array<BenchlingBlob>> {
  return TaskRunner.postJSON({
    url: '/1/api/instruments/instrument-data/blobs',
    data: {instrumentDataIds},
  }).then(({blobs}) => blobs);
}

export function getOrCreateFile(data: {
  instrumentDataId: string;
  folderId: string;
  connectionId: string;
}): JQueryPromise<string> {
  return TaskRunner.postJSON({url: '/1/api/instruments/instrument-data/file', data}).then(
    ({fileId}) => fileId
  );
}

export function parseInstrumentData(
  instrumentData: InstrumentData,
  delimiter: string,
  inferDataTypes?: boolean
): Promise<{columns: Array<string>; data_types?: Array<string>}> {
  return net.postJSONPromise({
    url: '/1/api/instruments/instrument-data/get-spreadsheet-columns',
    data: {
      instrumentDataId: instrumentData.id,
      delimiter,
      inferDataTypes,
    },
  });
}

// TODO generalize beyond Chromeleon
export function createBrowseFilesInstrumentQuery(
  connectionId: string,
  credentials?: {username: string; password: string}
): Promise<{instrumentQueryId: string}> {
  return net.postJSONPromise({
    url: '/1/api/instruments/instrument-queries',
    data: {
      connectionId,
      command: 'BROWSE_FILES',
      params: credentials ?? {},
    },
  });
}

export function createCopyObjectInstrumentQuery(
  instrumentData: InstrumentData,
  params: {[key: string]: string | boolean} | null,
  runId?: string
): Promise<{instrumentQueryId: string}> {
  if (!params) {
    params = {};
  }
  params.file_id = instrumentData.id;
  if (params?.username) {
    // the instruments service only needs the username and automatically replaces this when sending the actual query
    params.password = '******';
  }
  return net.postJSONPromise({
    url: '/1/api/instruments/instrument-queries',
    data: {
      connectionId: instrumentData.connectionId,
      command: 'COPY_OBJECT',
      runId,
      params,
    },
  });
}

export function createCopyObjectInstrumentQueryFromRun(
  connectionId: string,
  runId: string,
  params?: {[key: string]: string}
): Promise<{instrumentQueryId: string}> {
  return net.postJSONPromise({
    url: '/1/api/instruments/instrument-queries',
    data: {
      connectionId,
      command: 'COPY_OBJECT',
      runId,
      params: params ?? {},
    },
  });
}

export function createSendInputFileInstrumentQuery(
  automationInputFileId: string,
  connectionId: string,
  username: string | null
): Promise<{instrumentQueryId: string}> {
  const paramsDict =
    username == null
      ? {}
      : {
          username,
          // the instruments service only needs the username and automatically replaces this when sending the actual query
          password: '******',
        };
  return net.postJSONPromise({
    url: '/1/api/instruments/instrument-queries',
    data: {
      automationInputFileId,
      connectionId,
      command: 'SEND_INPUT_FILE',
      params: paramsDict,
    },
  });
}

export function getInstrumentQuery(instrumentQueryId: string): Promise<InstrumentQuery> {
  return net.getJSONPromise(`/1/api/instruments/instrument-queries/${instrumentQueryId}`);
}

export async function pollUntil<T>(
  runTask: () => Promise<T>,
  isComplete: (response: T) => boolean,
  iterationWaitTime: number = ITERATION_WAIT_TIME,
  maxWaitTime: number = MAX_WAIT_TIME,
  maxRetryAttempts: number = MAX_RETRY_ATTEMPTS
): Promise<T> {
  let totalRetries = 0;
  let totalWaitTime = 0;

  while (totalRetries < maxRetryAttempts && totalWaitTime < maxWaitTime) {
    const response = await runTask();
    if (isComplete(response)) {
      return response;
    }

    totalRetries += 1;
    totalWaitTime += iterationWaitTime;
    await sleep(iterationWaitTime);
  }

  if (totalRetries >= maxRetryAttempts) {
    throw new Error(`Polling failed after ${totalRetries} retries. Please try again later.`);
  }

  throw new Error(`Polling failed after ${totalWaitTime} milliseconds. Please try again later.`);
}

export async function pollInstrumentQueryStatus(instrumentQueryId: string): Promise<InstrumentQuery> {
  return await pollUntil<InstrumentQuery>(
    () => getInstrumentQuery(instrumentQueryId),
    (response: InstrumentQuery) =>
      response.status === InstrumentQueryStatus.DONE || response.status === InstrumentQueryStatus.ERROR
  );
}
